import React, { useState, useEffect, useCallback } from 'react';
import { ShieldCheck, PiggyBank, Landmark, Send, Loader2, CheckCircle2, AlertTriangle, FileText } from 'lucide-react';

/* ------------------------------------------------------------------ *
 * "Chase the onboarding paperwork" control (admin).
 *
 * A night sorter uploads their signed TFN declaration and super choice form in the
 * portal's Documents tab (the TFN read off the form goes to the encrypted vault); their
 * bank account sits on the sorters row. A new starter must upload both forms — see
 * _shared/tax-super-chase formsOnly. Nothing here ever shows or moves those
 * values: it reads the booleans, and sends ONE SMS + email naming what is missing,
 * pointing at the portal. The request is recorded so the office can see it was
 * asked for, and the sorter's portal raises a banner until it's done.
 * ------------------------------------------------------------------ */

const VAULT_URL = '/.netlify/functions/sorter-vault';
const post = (body) => fetch(VAULT_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).then(r => r.json());

// Answer ids → the words the ATO form uses, so the office reads back what the sorter
// actually saw. Mirrors _shared/tfn-declaration.js.
const BASIS_LABEL = {
  fullTime: 'Full-time employment', partTime: 'Part-time employment', casual: 'Casual employment',
  superIncomeStream: 'Superannuation or annuity income stream', labourHire: 'Labour hire',
};
const RESIDENCY_LABEL = {
  resident: 'Australian resident for tax purposes', foreign: 'Foreign resident for tax purposes',
  workingHoliday: 'Working holiday maker',
};
const TFN_BASIS_LABEL = {
  quoted: 'TFN quoted', applied: 'Applied for / enquiring with the ATO',
  under18: 'Exempt — under 18 and under the tax-free threshold', pension: 'Exempt — receives a pension, benefit or allowance',
};
const yn = (v) => (v ? 'Yes' : 'No');
// "a, b and c" — three items joined by 'and' twice reads badly.
const listPhrase = (a) => (a.length < 2 ? (a[0] || '') : a.slice(0, -1).join(', ') + ' and ' + a[a.length - 1]);

const pill = (bg, fg) => ({ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '.7rem', fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: bg, color: fg });
const okPill = pill('rgba(34,197,94,0.12)', 'var(--hp-green, #22C55E)');
const warnPill = pill('rgba(245,158,11,0.14)', 'var(--hp-amber, #F59E0B)');
const shortDate = (v) => (v ? new Date(v).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

export default function SorterTaxSuperRequest({ login, name, by }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);   // { ok, text }
  const [decl, setDecl] = useState(null); // the declaration read back for inspection
  const [declBusy, setDeclBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    post({ op: 'onboardingStatus', login })
      .then(d => { if (d && d.success) setState(d); else setMsg({ ok: false, text: (d && d.error) || 'Could not read status.' }); })
      .catch(e => setMsg({ ok: false, text: e.message }))
      .finally(() => setLoading(false));
  }, [login]);
  useEffect(() => { load(); }, [load]);

  async function viewDeclaration() {
    if (decl) { setDecl(null); return; }        // toggle closed
    setDeclBusy(true); setMsg(null);
    try {
      const d = await post({ op: 'getDeclaration', login, by: by || 'admin' });
      if (!d.success) throw new Error(d.error || 'Could not read the declaration.');
      setDecl(d);
    } catch (e) { setMsg({ ok: false, text: e.message }); }
    finally { setDeclBusy(false); }
  }

  async function request() {
    setBusy(true); setMsg(null);
    try {
      const d = await post({ op: 'requestOnboarding', login, by: by || 'admin' });
      if (!d.success) throw new Error(d.error || 'Could not send the request.');
      if (d.nothingOutstanding) {
        setMsg({ ok: true, text: 'Nothing outstanding — tax, super and bank details are all on file.' });
      } else {
        const sent = [d.request.sms && 'SMS', d.request.email && 'email'].filter(Boolean);
        setMsg(sent.length
          ? { ok: true, text: `Asked ${name || login} for ${listPhrase(d.request.items)} by ${sent.join(' + ')}.` }
          // No channel fired: no mobile/email on the record, or Twilio/Gmail not configured.
          : { ok: false, text: 'Request recorded, but nothing could be sent — check their mobile and email are on file.' });
      }
      load();
    } catch (e) { setMsg({ ok: false, text: e.message }); }
    finally { setBusy(false); }
  }

  const tfnOk = !!state?.tfn?.has;
  const superOk = !!state?.superan?.has;
  const bankOk = !!state?.bank?.has;
  const req = state?.request;
  // "Applied for" is a complete declaration but the number is still owed — and after
  // 28 days the top withholding rate applies, so it must not read as finished.
  const awaitingNumber = state?.tfn?.status === 'Applied for';
  const allDone = tfnOk && superOk && bankOk && !awaitingNumber;

  return (
    <div style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid var(--hp-border)', background: 'rgba(201,168,76,0.06)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700, fontSize: '.84rem' }}>Tax, super &amp; bank</span>
        {loading ? <span style={{ color: 'var(--hp-dim)', fontSize: '.76rem' }}>checking…</span> : (
          <>
            <span style={tfnOk && !awaitingNumber ? okPill : warnPill}>
              <ShieldCheck size={12} /> {tfnOk
                ? `${state.tfn.status || 'TFN on file'}${state.tfn.last3 ? ` ••• ${state.tfn.last3}` : ''}${state.tfn.declaredAt ? ` · declared ${shortDate(state.tfn.declaredAt)}` : ''}`
                : (state.formsOnly ? 'TFN form not uploaded' : 'TFN not provided')}
            </span>
            <span style={superOk ? okPill : warnPill}>
              <PiggyBank size={12} /> {superOk ? (state.superan.fund || 'fund nominated') : (state.formsOnly ? 'super form not uploaded' : 'no super fund')}
            </span>
            {/* Where the wages go. Masked here — the numbers reveal through the
                vault admin secret in the Bank & super panel, not from a status pill. */}
            <span style={bankOk ? okPill : warnPill}>
              <Landmark size={12} /> {bankOk
                ? `${state.bank.institution || 'account'}${state.bank.last3 ? ` \u2022\u2022\u2022\u2022${state.bank.last3}` : ''}`
                : 'no bank details'}
            </span>
          </>
        )}
        {!loading && !allDone && (
          <button
            onClick={request}
            disabled={busy}
            style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 6, border: 'none', background: 'var(--hp-gold)', color: '#1a1a2e', fontWeight: 700, cursor: busy ? 'wait' : 'pointer', fontSize: '.78rem' }}
          >
            {busy ? <Loader2 size={13} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Send size={13} />}
            {req ? 'Send reminder' : 'Request from sorter'}
          </button>
        )}
      </div>

      {!loading && awaitingNumber && state.tfn.topRateFrom && (
        <div style={{ fontSize: '.72rem', color: 'var(--hp-amber, #F59E0B)', marginTop: 6 }}>
          They declared they had applied for a TFN. If it hasn't arrived by {shortDate(state.tfn.topRateFrom)}, withhold at the top rate.
        </div>
      )}

      {/* A vault record can exist without counting (a new starter's typed declaration
          from before uploads were required) — still worth being able to read. */}
      {!loading && (tfnOk || state?.tfn?.status) && (
        <div style={{ marginTop: 8 }}>
          <button onClick={viewDeclaration} disabled={declBusy}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 6, border: '1px solid var(--hp-border)', background: 'transparent', color: 'var(--hp-dim)', fontWeight: 700, cursor: 'pointer', fontSize: '.74rem' }}>
            {declBusy ? <Loader2 size={12} style={{ animation: 'spin 0.8s linear infinite' }} /> : <FileText size={12} />}
            {decl ? 'Hide declaration' : 'View declaration'}
          </button>
          {decl && (
            <div style={{ marginTop: 8, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', fontSize: '.78rem' }}>
              {[
                // Office-entered and pre-Aug-2026 records have no answer set — say so
                // rather than rendering a row of blanks that looks like a declaration.
                ['Tax file number', (TFN_BASIS_LABEL[decl.declaration.tfnBasis] || (decl.declaration.enteredByOffice ? decl.declaration.source : 'On file')) + (decl.last3 ? ` ••• ${decl.last3}` : '')],
                ['Name', [decl.declaration.title, decl.declaration.firstName, decl.declaration.otherNames, decl.declaration.surname].filter(Boolean).join(' ')],
                ['Previous family name', decl.declaration.previousSurname],
                ['Date of birth', decl.declaration.dob],
                ['Home address', [decl.declaration.addressLine, decl.declaration.suburb, decl.declaration.state, decl.declaration.postcode].filter(Boolean).join(', ')],
                ['Basis of payment', BASIS_LABEL[decl.declaration.paymentBasis]],
                ['Residency', RESIDENCY_LABEL[decl.declaration.residency]],
                ['Tax-free threshold', yn(decl.declaration.taxFreeThreshold)],
                ['Seniors & pensioners offset', yn(decl.declaration.seniorsOffset)],
                ['Zone / overseas forces offset', yn(decl.declaration.zoneOffset)],
                ['Study or training support loan', yn(decl.declaration.stsl)],
              ].filter(([, v]) => v).map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '3px 0', borderBottom: '1px solid var(--hp-border)' }}>
                  <span style={{ color: 'var(--hp-dim)' }}>{k}</span>
                  <span style={{ textAlign: 'right' }}>{v}</span>
                </div>
              ))}
              <div style={{ marginTop: 8, color: 'var(--hp-dim)', fontSize: '.72rem' }}>
                {decl.declaration.signedName ? (
                  <>“{decl.declaration.declarationText || 'I declare that the information I have given is true and correct.'}” — signed {decl.declaration.signedName}
                    {decl.declaredAt ? ` on ${shortDate(decl.declaredAt)}` : ''}. </>
                ) : (
                  <>Not signed in the portal — the declaration itself is held outside the app
                    {decl.declaration.declaredBy ? `, recorded by ${decl.declaration.declaredBy}` : ''}
                    {decl.declaredAt ? ` on ${shortDate(decl.declaredAt)}` : ''}. </>
                )}
                Viewing this is logged to the vault audit.
              </div>
            </div>
          )}
        </div>
      )}

      {!loading && !allDone && (
        <div style={{ fontSize: '.7rem', color: 'var(--hp-dim)', marginTop: 6 }}>
          {req
            ? `Last asked ${shortDate(req.at)}${req.by ? ` by ${req.by}` : ''} — ${[req.sms && 'SMS', req.email && 'email'].filter(Boolean).join(' + ') || 'nothing sent'}. They complete it in the portal's Documents tab.`
            : 'Texts + emails them a link to complete it in the portal. Nothing sensitive is sent — they type the details in there themselves.'}
        </div>
      )}

      {msg && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: '.76rem', color: msg.ok ? 'var(--hp-green, #22C55E)' : '#ef4444' }}>
          {msg.ok ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />} {msg.text}
        </div>
      )}
    </div>
  );
}
